import {
    Batch,
    BatchCallback,
} from '@/features/pos/components/Cart/hooks/useChangeCartItemQuantity/types'
import debounce from 'lodash/debounce'
import { useCallback, useRef } from 'react'

export const useBatch = (
    callback: BatchCallback,
    onExecuteEffect: (id: string, quantityChange: number) => void,
    skip: (id: string, quantityChange: number) => boolean,
    onNoPendingQuantity: (id: string) => void
) => {
    const batchesRef = useRef<Record<string, Batch>>({})

    return useCallback(
        (id: string, quantityChange: number) => {
            if (skip(id, quantityChange)) return

            let batch = batchesRef.current[id]

            if (!batch) {
                batchesRef.current[id] = {
                    itemId: id,
                    pendingQuantity: 0,
                    inFlight: false,
                    queuedQuantity: 0,
                    debouncedFn: debounce(() => {
                        const { pendingQuantity } = batch

                        if (pendingQuantity === 0) return onNoPendingQuantity(id)

                        batch.pendingQuantity = 0

                        if (batch.inFlight) {
                            batch.queuedQuantity += pendingQuantity

                            return
                        }

                        batch.inFlight = true

                        const handleCallback = (quantity: number) => {
                            const handleCompleted = () => {
                                batch.inFlight = false

                                if (batch.queuedQuantity !== 0) {
                                    const { queuedQuantity } = batch

                                    batch.queuedQuantity = 0

                                    handleCallback(queuedQuantity)
                                } else {
                                    delete batchesRef.current[id]
                                }
                            }

                            const handleError = (callback: (quantityChange: number) => void) => {
                                batch.debouncedFn.cancel()

                                const quantityChange =
                                    quantity + batch.queuedQuantity + batch.pendingQuantity

                                batch.inFlight = false
                                batch.queuedQuantity = 0
                                batch.pendingQuantity = 0

                                delete batchesRef.current[id]

                                callback(quantityChange)

                                onNoPendingQuantity(id)
                            }

                            callback(id, quantity, handleCompleted, handleError)
                        }

                        handleCallback(pendingQuantity)
                    }, 600),
                }

                batch = batchesRef.current[id]
            }

            batch.pendingQuantity += quantityChange

            onExecuteEffect(id, quantityChange)

            batch.debouncedFn()
        },
        [callback, onExecuteEffect, skip, onNoPendingQuantity]
    )
}
